import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Camera, CheckCircle2, KeyRound, User } from 'lucide-react';
import type { UpdateMeRequest } from '@command-room/shared';
import { useMe, useUpdateMe, useUploadAvatar } from '@/api/queries';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ─── Avatar upload ────────────────────────────────────────────────────────────

function AvatarSection() {
  const { data } = useMe();
  const uploadAvatar = useUploadAvatar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const user = data?.user;
  if (!user) return null;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    uploadAvatar.mutate(file, { onSuccess: () => setPreview(null) });
  }

  const avatarSrc = preview ?? user.avatar_url ?? undefined;

  return (
    <div className="flex items-center gap-5">
      <div className="relative">
        <Avatar className="h-20 w-20 ring-2 ring-border">
          {avatarSrc && <AvatarImage src={avatarSrc} alt={user.full_name} />}
          <AvatarFallback className="text-lg">{initials(user.full_name)}</AvatarFallback>
        </Avatar>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploadAvatar.isPending}
          className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors disabled:opacity-50"
          title="Change avatar"
        >
          <Camera className="h-3.5 w-3.5" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={handleFile}
        />
      </div>
      <div>
        <p className="font-semibold text-base">{user.full_name}</p>
        <p className="text-sm text-muted-foreground">{user.email}</p>
        <p className="text-xs text-muted-foreground mt-0.5 capitalize">{user.role}</p>
        {uploadAvatar.error && (
          <p className="text-xs text-destructive mt-1">
            {(uploadAvatar.error as Error).message}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Profile form ─────────────────────────────────────────────────────────────

type ProfileFormData = {
  full_name: string;
  slack_user_id: string;
  slack_handle: string;
  department: string;
  timezone: string;
  locale: string;
};

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

function ProfileForm() {
  const { data } = useMe();
  const updateMe = useUpdateMe();
  const [saved, setSaved] = useState(false);

  const user = data?.user;

  const { register, handleSubmit, reset, formState: { isDirty } } = useForm<ProfileFormData>({
    defaultValues: {
      full_name: user?.full_name ?? '',
      slack_user_id: user?.slack_user_id ?? '',
      slack_handle: user?.slack_handle ?? '',
      department: user?.department ?? '',
      timezone: user?.timezone ?? 'UTC',
      locale: user?.locale ?? 'en',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        full_name: user.full_name,
        slack_user_id: user.slack_user_id ?? '',
        slack_handle: user.slack_handle ?? '',
        department: user.department ?? '',
        timezone: user.timezone,
        locale: user.locale,
      });
    }
  }, [user, reset]);

  async function onSubmit(data: ProfileFormData) {
    const body: UpdateMeRequest = {
      full_name: data.full_name || undefined,
      slack_user_id: data.slack_user_id || null,
      slack_handle: data.slack_handle || null,
      department: data.department || null,
      timezone: data.timezone || undefined,
      locale: data.locale || undefined,
    };
    await updateMe.mutateAsync(body);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    reset(data);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="full_name">Display name</Label>
          <Input id="full_name" {...register('full_name', { required: true })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="department">Department</Label>
          <Input id="department" {...register('department')} placeholder="Engineering" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="slack_handle">Slack handle</Label>
          <Input id="slack_handle" {...register('slack_handle')} placeholder="@you" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="slack_user_id">
            Slack user ID
            <span className="ml-1.5 text-xs text-muted-foreground font-normal">(for @mention attribution)</span>
          </Label>
          <Input
            id="slack_user_id"
            {...register('slack_user_id')}
            placeholder="U0123456"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Find in Slack: click profile → ⋯ → Copy member ID
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="timezone">Timezone</Label>
          <select
            id="timezone"
            {...register('timezone')}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="locale">Locale</Label>
          <select
            id="locale"
            {...register('locale')}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="en">English (en)</option>
            <option value="nl">Dutch (nl)</option>
            <option value="de">German (de)</option>
            <option value="fr">French (fr)</option>
            <option value="es">Spanish (es)</option>
            <option value="pt">Portuguese (pt)</option>
          </select>
        </div>
      </div>

      {updateMe.error && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {(updateMe.error as Error).message}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!isDirty || updateMe.isPending}>
          {updateMe.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            Saved
          </span>
        )}
      </div>
    </form>
  );
}

// ─── Password change form ─────────────────────────────────────────────────────

type PasswordFormData = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

function PasswordForm() {
  const updateMe = useUpdateMe();
  const [done, setDone] = useState(false);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<PasswordFormData>();

  const newPw = watch('new_password');

  async function onSubmit(data: PasswordFormData) {
    await updateMe.mutateAsync({
      current_password: data.current_password,
      new_password: data.new_password,
    });
    setDone(true);
    setTimeout(() => setDone(false), 4000);
    reset();
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-sm">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="current_password">Current password</Label>
        <Input
          id="current_password"
          type="password"
          {...register('current_password', { required: true })}
          className={errors.current_password ? 'border-destructive' : ''}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new_password">New password</Label>
        <Input
          id="new_password"
          type="password"
          {...register('new_password', { required: true, minLength: 12 })}
          placeholder="Minimum 12 characters"
          className={errors.new_password ? 'border-destructive' : ''}
        />
        {errors.new_password?.type === 'minLength' && (
          <p className="text-xs text-destructive">Minimum 12 characters</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="confirm_password">Confirm new password</Label>
        <Input
          id="confirm_password"
          type="password"
          {...register('confirm_password', {
            required: true,
            validate: (v) => v === newPw || 'Passwords do not match',
          })}
          className={errors.confirm_password ? 'border-destructive' : ''}
        />
        {errors.confirm_password && (
          <p className="text-xs text-destructive">{errors.confirm_password.message}</p>
        )}
      </div>

      {updateMe.error && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {(updateMe.error as Error).message}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={updateMe.isPending}>
          {updateMe.isPending ? 'Updating…' : 'Update password'}
        </Button>
        {done && (
          <span className="flex items-center gap-1 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            Password updated
          </span>
        )}
      </div>
    </form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ProfilePage() {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Your profile</h1>
        <p className="text-sm text-muted-foreground">
          Identity, Slack mapping, and security settings.
        </p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <AvatarSection />
        </CardContent>
      </Card>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="h-3.5 w-3.5 mr-1.5" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security">
            <KeyRound className="h-3.5 w-3.5 mr-1.5" />
            Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile information</CardTitle>
            </CardHeader>
            <CardContent>
              <ProfileForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Change password</CardTitle>
            </CardHeader>
            <CardContent>
              <PasswordForm />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
